import { useRouter } from "next/router";
import { useHubEvent } from "../../../common/api/hub";
import {
    ANNOUNCEMENTS_HUB_EVENTS,
    ANNOUNCEMENTS_HUB_URL,
    ANNOUNCEMENT_SEVERITIES,
    ANNOUNCEMENT_STATUSES,
    ANNOUNCEMENT_TYPES,
} from "../constants";
import { Announcement } from "../models";
import { useAnnouncementsStore } from "../store";
import { getAnnouncementTranslation } from "../utils";
import { useLiveAnnouncements } from "./useLiveAnnouncements";

const isDisplayable = (
    announcement: Announcement | null | undefined
): announcement is Announcement =>
    !!announcement &&
    announcement.status === ANNOUNCEMENT_STATUSES.LIVE &&
    announcement.type === ANNOUNCEMENT_TYPES.BANNER;

interface UseLiveAnnouncementsStreamProps {
    onInit?: (announcement: Announcement | null) => void;
    onMessage?: (payload: Announcement | null) => void;
}

/**
 * useLiveAnnouncementsStream
 *   → useHubEvent → SignalR start
 *     → /api/announcements-hub (HTTP + WS)
 *       → server.js proxy → APIM → hub
 *         ← event → onMessage → Zustand → banner
 */
export const useLiveAnnouncementsStream = (
    props: UseLiveAnnouncementsStreamProps
) => {
    const { onInit, onMessage } = props;
    const { locale } = useRouter();

    const updateStore = (announcement: Announcement | null) => {
        const {
            dismissedAnnouncementId,
            setLiveAnnouncement,
            setDismissedAnnouncementId,
        } = useAnnouncementsStore.getState();

        if (!announcement) {
            setLiveAnnouncement(null);
            return;
        }

        if (announcement.id === dismissedAnnouncementId) return;

        const translation = getAnnouncementTranslation(
            announcement.translations,
            locale
        );
        if (!translation) return;

        setLiveAnnouncement({
            id: announcement.id,
            title: translation.title,
            description: translation.description,
            severity: announcement.severity ?? ANNOUNCEMENT_SEVERITIES.INFO,
            type: announcement.type,
            onHide: () => {
                setDismissedAnnouncementId(announcement.id);
                setLiveAnnouncement(null);
            },
        });
    };

    useLiveAnnouncements({
        onSuccess: (data) => {
            const announcement = data.find(isDisplayable) ?? null;
            updateStore(announcement);
            onInit?.(announcement);
        },
    });

    useHubEvent<Announcement | null>(
        ANNOUNCEMENTS_HUB_URL,
        ANNOUNCEMENTS_HUB_EVENTS.ANNOUNCEMENT_CHANGED,
        (payload) => {
            const payloadId = payload?.id;

            if (isDisplayable(payload)) {
                updateStore(payload);
            } else {
                const { liveAnnouncement } = useAnnouncementsStore.getState();
                if (payloadId === liveAnnouncement?.id) {
                    updateStore(null);
                }
            }
            onMessage?.(payload);
        }
    );
};
